/**
 * Custom Booking Form Component
 * Handles date selection with Flatpickr, document uploads, and booking creation
 */

class CustomBookingForm extends HTMLElement {
  constructor() {
    super();
    this.apiUrl = this.dataset.apiUrl;
    this.apiKey = this.dataset.apiKey;
    this.vehicleId = this.dataset.vehicleId;
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.dailyRate = parseFloat(this.dataset.dailyRate) || 0;
    this.vehicleName = this.dataset.vehicleName;
    this.showDropdown = this.dataset.showDropdown === 'true';

    // Parse vehicles from data attribute
    try {
      this.vehicles = JSON.parse(this.dataset.vehicles || '[]');
    } catch (e) {
      this.vehicles = [];
    }

    this.uploadedDocs = {};
    this.blockedDates = [];
    this.blockedRanges = [];
    this.isSubmitting = false;
    this.flatpickrInstance = null;
    this.durationType = 'full_day';
    this.FOUR_HOUR_FACTOR = 0.5;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.dateRangeInput = this.querySelector('input[name="date_range"]');
    this.pickupInput = this.querySelector('input[name="pickup_date"]');
    this.returnInput = this.querySelector('input[name="return_date"]');
    this.rentalDaysInput = this.querySelector('input[name="rental_days"]');
    this.submitBtn = this.querySelector('button[type="submit"]');
    this.errorEl = this.querySelector('[id^="error-"]');
    this.dateSummaryEl = this.querySelector('[id^="date-summary-"]');
    this.rentalDaysRow = this.querySelector('[id^="rental-days-row-"]');
    this.totalRow = this.querySelector('[id^="total-row-"]');
    this.vehicleSelect = this.querySelector('select[name="vehicle_select"]');
    this.vehiclePreview = this.querySelector('.custom-booking__vehicle-preview');
    this.vehicleRow = this.querySelector('.custom-booking__summary-row--vehicle');
    this.dailyRateEl = this.querySelector('.custom-booking__daily-rate');
    this.selectedVehicleEl = this.querySelector('.custom-booking__selected-vehicle');
    this.formSections = this.querySelector('.custom-booking__form-sections');
    this.durationRadios = this.querySelectorAll('input[name="duration_type"]');

    this.init();
  }

  async init() {
    this.initUploadListeners();
    this.initFormSubmit();
    this.initDurationListeners();

    // 1. Duration mode from the deep-link query param, BEFORE any date picker
    //    is built (the picker's range/single mode depends on it).
    const params = new URLSearchParams(window.location.search);
    if (params.get('duration') === '4h') {
      const fourHour = this.querySelector('input[name="duration_type"][value="four_hour"]');
      if (fourHour) fourHour.checked = true;
    }
    this.durationType = this.getSelectedDuration();

    // 2. Vehicle + date picker
    if (this.showDropdown && this.vehicleSelect) {
      this.initVehicleDropdown();
      // Preselect a vehicle from the query param AFTER the change listener is
      // attached, so its handler builds the picker (in the mode set above).
      const vehicleParam = params.get('vehicle');
      if (vehicleParam) {
        const opt = Array.from(this.vehicleSelect.options).find((o) => o.value === vehicleParam);
        if (opt) {
          this.vehicleSelect.value = vehicleParam;
          this.vehicleSelect.dispatchEvent(new Event('change'));
        }
      }
    } else {
      // No dropdown - init date picker immediately (durationType already set)
      this.initDatePicker();
      if (this.vehicleId && this.apiKey) {
        await this.fetchBlockedDates();
      }
    }
  }

  getSelectedDuration() {
    const checked = this.querySelector('input[name="duration_type"]:checked');
    return checked ? checked.value : 'full_day';
  }

  initDurationListeners() {
    this.durationRadios.forEach((r) => {
      r.addEventListener('change', () => {
        this.durationType = this.getSelectedDuration();
        // Rebuild the date picker for the new mode and reset any selection.
        // Blocked ranges persist on `this`, so the rebuilt picker keeps them.
        if (this.flatpickrInstance) {
          this.flatpickrInstance.destroy();
          this.flatpickrInstance = null;
        }
        if (this.pickupInput) this.pickupInput.value = '';
        if (this.returnInput) this.returnInput.value = '';
        if (this.dateRangeInput) this.dateRangeInput.value = '';
        this.clearSummary();
        this.initDatePicker();
      });
    });
  }

  initVehicleDropdown() {
    this.vehicleSelect.addEventListener('change', async (e) => {
      const selectedOption = e.target.options[e.target.selectedIndex];

      if (!selectedOption.value) {
        this.hideVehiclePreview();
        this.hideFormSections();
        return;
      }

      this.vehicleId = selectedOption.value;
      this.productId = selectedOption.dataset.productId;
      this.variantId = selectedOption.dataset.variantId;
      this.dailyRate = parseFloat(selectedOption.dataset.dailyRate) || 0;
      this.vehicleName = selectedOption.dataset.name;

      this.showVehiclePreview(selectedOption.dataset.image, this.vehicleName);
      this.updateDailyRateDisplay();
      this.showFormSections();

      // Reset and reinit date picker with new blocked dates
      if (this.flatpickrInstance) {
        this.flatpickrInstance.destroy();
        this.flatpickrInstance = null;
      }
      this.blockedDates = [];
      this.blockedRanges = [];

      if (this.vehicleId && this.apiKey) {
        await this.fetchBlockedDates();
      }

      this.initDatePicker();
    });
  }

  showFormSections() {
    if (this.formSections) {
      this.formSections.style.display = 'block';
    }
  }

  hideFormSections() {
    if (this.formSections) {
      this.formSections.style.display = 'none';
    }
  }

  showVehiclePreview(imageUrl, name) {
    if (this.vehiclePreview && imageUrl) {
      const img = this.vehiclePreview.querySelector('img');
      const nameEl = this.vehiclePreview.querySelector('.custom-booking__vehicle-name');
      if (img) img.src = imageUrl;
      if (nameEl) nameEl.textContent = name;
      this.vehiclePreview.style.display = 'flex';
    }

    if (this.vehicleRow) {
      this.vehicleRow.style.display = 'flex';
      if (this.selectedVehicleEl) {
        this.selectedVehicleEl.textContent = name;
      }
    }
  }

  hideVehiclePreview() {
    if (this.vehiclePreview) {
      this.vehiclePreview.style.display = 'none';
    }
    if (this.vehicleRow) {
      this.vehicleRow.style.display = 'none';
    }
  }

  updateDailyRateDisplay() {
    if (this.dailyRateEl) {
      this.dailyRateEl.textContent = this.formatCurrency(this.dailyRate);
    }
  }

  initDatePicker() {
    if (!this.dateRangeInput || typeof flatpickr === 'undefined') {
      console.warn('Flatpickr not loaded or date input not found');
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Build disable array from blocked ranges
    const disabledDates = this.buildDisabledDates();

    this.flatpickrInstance = flatpickr(this.dateRangeInput, {
      mode: this.durationType === 'four_hour' ? 'single' : 'range',
      minDate: 'today',
      dateFormat: 'M j, Y',
      disable: disabledDates,
      showMonths: 1,
      static: true,
      appendTo: this.closest('.custom-booking'),
      onChange: (selectedDates, dateStr) => {
        if (this.durationType === 'four_hour') {
          if (selectedDates.length === 1) {
            const iso = this.formatDateISO(selectedDates[0]);
            this.pickupInput.value = iso;
            this.returnInput.value = iso; // single date blocks that day
            this.updateSummary(selectedDates[0], selectedDates[0]);
          } else {
            this.pickupInput.value = '';
            this.returnInput.value = '';
            this.clearSummary();
          }
          return;
        }
        if (selectedDates.length === 2) {
          const [pickup, returnDate] = selectedDates;
          this.pickupInput.value = this.formatDateISO(pickup);
          this.returnInput.value = this.formatDateISO(returnDate);
          this.updateSummary(pickup, returnDate);
        } else {
          this.pickupInput.value = '';
          this.returnInput.value = '';
          this.clearSummary();
        }
      },
      onDayCreate: (dObj, dStr, fp, dayElem) => {
        // Add blocked class to disabled dates for red styling
        if (dayElem.classList.contains('flatpickr-disabled')) {
          const date = dayElem.dateObj;
          if (date && this.isDateInBlockedRange(date)) {
            dayElem.classList.add('blocked');
          }
        }
      }
    });
  }

  buildDisabledDates() {
    const disabled = [];

    // Add each blocked range
    for (const range of this.blockedRanges) {
      disabled.push({
        from: range.start,
        to: range.end
      });
    }

    return disabled;
  }

  isDateInBlockedRange(date) {
    const dateTime = date.getTime();
    for (const range of this.blockedRanges) {
      if (dateTime >= range.start.getTime() && dateTime <= range.end.getTime()) {
        return true;
      }
    }
    return false;
  }

  formatDateISO(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  updateSummary(pickup, returnDate) {
    const isFourHour = this.durationType === 'four_hour';
    const days = isFourHour ? 1 : this.calculateDays(pickup, returnDate);
    const total = isFourHour ? this.dailyRate * this.FOUR_HOUR_FACTOR : days * this.dailyRate;

    this.rentalDaysInput.value = days;

    if (this.dateSummaryEl) {
      this.dateSummaryEl.textContent = isFourHour
        ? '4 hours rental'
        : `${days} day${days > 1 ? 's' : ''} rental`;
    }

    if (this.rentalDaysRow) {
      this.rentalDaysRow.style.display = 'flex';
      const daysEl = this.rentalDaysRow.querySelector('.custom-booking__rental-days');
      if (daysEl) daysEl.textContent = days;
    }

    if (this.totalRow) {
      this.totalRow.style.display = 'flex';
      const totalEl = this.totalRow.querySelector('.custom-booking__total');
      if (totalEl) totalEl.textContent = this.formatCurrency(total);
    }
  }

  clearSummary() {
    if (this.dateSummaryEl) {
      this.dateSummaryEl.textContent = '';
    }
    if (this.rentalDaysRow) {
      this.rentalDaysRow.style.display = 'none';
    }
    if (this.totalRow) {
      this.totalRow.style.display = 'none';
    }
  }

  calculateDays(pickup, returnDate) {
    const p = new Date(pickup);
    const r = new Date(returnDate);
    const diffTime = r - p;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  initUploadListeners() {
    const uploads = this.querySelectorAll('.custom-booking__upload');

    uploads.forEach(upload => {
      const input = upload.querySelector('input[type="file"]');
      const docType = upload.dataset.docType;

      input.addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const preview = upload.querySelector('.custom-booking__upload-preview');
        const reader = new FileReader();
        reader.onload = (ev) => {
          preview.innerHTML = `<img src="${ev.target.result}" alt="Preview">`;
          preview.style.display = 'block';
        };
        reader.readAsDataURL(file);

        upload.classList.add('custom-booking__upload--uploaded');
        upload.querySelector('.custom-booking__upload-status').textContent = 'Uploaded';

        this.uploadedDocs[docType] = file;
      });
    });
  }

  initFormSubmit() {
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (this.isSubmitting) return;
      this.isSubmitting = true;

      this.showLoading(true);
      this.hideError();

      try {
        if (!this.validateForm()) {
          this.isSubmitting = false;
          this.showLoading(false);
          return;
        }

        const docUrls = await this.uploadDocuments();
        const checkoutUrl = await this.createBooking(docUrls);

        // Small delay for Shopify to finalize draft order
        await new Promise(resolve => setTimeout(resolve, 1500));
        window.location.href = checkoutUrl;
      } catch (error) {
        console.error('Booking error:', error);
        this.showError(error.message || 'Something went wrong. Please try again.');
        this.isSubmitting = false;
        this.showLoading(false);
      }
    });
  }

  validateForm() {
    if (this.showDropdown && this.vehicleSelect) {
      if (!this.vehicleSelect.value) {
        this.showError('Please select a vehicle.');
        return false;
      }
    }

    if (!this.vehicleId) {
      this.showError('No vehicle selected. Please select a vehicle.');
      return false;
    }

    const pickup = this.pickupInput.value;
    const returnDate = this.returnInput.value;
    const name = this.form.querySelector('input[name="customer_name"]').value;
    const email = this.form.querySelector('input[name="customer_email"]').value;
    const phone = this.form.querySelector('input[name="customer_phone"]').value;

    if (!pickup || !returnDate) {
      this.showError('Please select your rental dates.');
      return false;
    }

    if (!name || !email || !phone) {
      this.showError('Please fill in all contact information.');
      return false;
    }

    const requiredDocs = ['license_front', 'license_back', 'selfie'];
    for (const doc of requiredDocs) {
      if (!this.uploadedDocs[doc]) {
        this.showError(`Please upload your ${doc.replace('_', ' ')}.`);
        return false;
      }
    }

    return true;
  }

  async fetchBlockedDates() {
    try {
      const response = await fetch(
        `${this.apiUrl}/api/public/vehicles/${this.vehicleId}/blocked-dates?tenant_slug=exoticsbythebay`,
        {
          headers: {
            'X-API-Key': this.apiKey
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Parse blocked date ranges from API response
        // Expected format: [{start_date, end_date, booking_id}, ...]
        this.blockedRanges = [];

        const ranges = Array.isArray(data) ? data : (data.blocked_dates || []);

        for (const range of ranges) {
          if (range.start_date && range.end_date) {
            this.blockedRanges.push({
              start: new Date(range.start_date),
              end: new Date(range.end_date)
            });
          }
        }

        // If date picker already exists, update it
        if (this.flatpickrInstance) {
          this.flatpickrInstance.set('disable', this.buildDisabledDates());
        }
      }
    } catch (error) {
      console.warn('Could not fetch blocked dates:', error);
    }
  }

  async uploadDocuments() {
    const docUrls = {};

    for (const [docType, file] of Object.entries(this.uploadedDocs)) {
      const formData = new FormData();
      formData.append('file', file);

      const uploadUrl = `${this.apiUrl}/api/public/bookings/upload?folder=bookings/pending&doc_type=${docType}`;

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'X-API-Key': this.apiKey
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || `Failed to upload ${docType}`);
      }

      const data = await response.json();
      docUrls[docType] = data.url;
    }

    return docUrls;
  }

  async createBooking(docUrls) {
    const formData = new FormData(this.form);
    const days = parseInt(this.rentalDaysInput?.value) || this.calculateDays(
      new Date(formData.get('pickup_date')),
      new Date(formData.get('return_date'))
    );

    const isFourHour = this.durationType === 'four_hour';
    const effectiveDays = isFourHour ? 1 : days;
    const totalAmount = isFourHour ? this.dailyRate * this.FOUR_HOUR_FACTOR : days * this.dailyRate;

    const payload = {
      vehicle_id: this.vehicleId,
      shopify_product_id: `gid://shopify/Product/${this.productId}`,
      shopify_variant_id: `gid://shopify/ProductVariant/${this.variantId}`,
      pickup_date: formData.get('pickup_date'),
      return_date: formData.get('return_date'),
      customer_name: formData.get('customer_name'),
      customer_email: formData.get('customer_email'),
      customer_phone: formData.get('customer_phone'),
      rental_days: effectiveDays,
      daily_rate: this.dailyRate,
      total_amount: totalAmount,          // display value; backend recomputes authoritatively
      duration_type: this.durationType,   // 'full_day' | 'four_hour'
      rental_hours: isFourHour ? 4 : null,
      vehicle_name: this.vehicleName,
      doc_license_front: docUrls.license_front,
      doc_license_back: docUrls.license_back,
      doc_selfie_with_license: docUrls.selfie,
      doc_insurance_card: docUrls.insurance_card || null
    };

    const response = await fetch(`${this.apiUrl}/api/public/bookings/shopify-checkout`, {
      method: 'POST',
      headers: {
        'X-API-Key': this.apiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create booking');
    }

    const data = await response.json();

    if (!data.checkout_url) {
      throw new Error('No checkout URL received');
    }

    return data.checkout_url;
  }

  showLoading(loading) {
    const textEl = this.submitBtn.querySelector('.custom-booking__submit-text');
    const loadingEl = this.submitBtn.querySelector('.custom-booking__submit-loading');

    if (loading) {
      textEl.style.display = 'none';
      loadingEl.style.display = 'flex';
      this.submitBtn.disabled = true;
    } else {
      textEl.style.display = 'inline';
      loadingEl.style.display = 'none';
      this.submitBtn.disabled = false;
    }
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.style.display = 'block';
    this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  hideError() {
    this.errorEl.style.display = 'none';
  }
}

// Register custom element
if (!customElements.get('custom-booking-form')) {
  customElements.define('custom-booking-form', CustomBookingForm);
}
